/**
 * The cookie that tells the server what the visitor has already decided.
 *
 * The decision itself lives in the preference store, which is `localStorage`,
 * which the server cannot see. The consent surface is the largest element on
 * the entry page, and a surface that cannot be drawn until a script has run
 * lands after hydration — which is what held the largest paint open for
 * seconds (#531). Drawing it from the server needs the decision before a byte
 * of HTML is written, so it is mirrored here.
 *
 * It carries the decision and nothing else, is read for nothing else, and is
 * necessary in the sense the surface itself uses the word: the page cannot be
 * served correctly without it. It sits in `support/` beside the region registry
 * because the store that writes it and the server that reads it must agree on
 * the name, and neither may reach for the other.
 */

/** The cookie's name, written by the client and read by the server. */
export const CONSENT_COOKIE = "consent";

/** A year, matching the record itself: long enough not to re-ask by forgetting. */
export const CONSENT_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/** What the cookie carries: the version decided against, and each category. */
export interface ConsentCookie {
  version: number;
  analytics: "granted" | "denied";
  marketing: "granted" | "denied";
}

type Decision = ConsentCookie["analytics"];

function isDecision(value: string | undefined): value is Decision {
  return value === "granted" || value === "denied";
}

/**
 * `<version>.<analytics>.<marketing>` — short enough to read in a header and
 * rigid enough that anything else is treated as no cookie at all.
 */
export function formatConsentCookie(value: ConsentCookie): string {
  return `${value.version}.${value.analytics}.${value.marketing}`;
}

/** The decision a cookie carries, or `null` for anything uninterpretable. */
export function parseConsentCookie(
  raw: string | null | undefined,
): ConsentCookie | null {
  if (!raw) return null;
  const [version, analytics, marketing] = raw.split(".");
  const parsedVersion = Number(version);
  if (!Number.isInteger(parsedVersion) || parsedVersion < 1) return null;
  if (!isDecision(analytics) || !isDecision(marketing)) return null;
  return { version: parsedVersion, analytics, marketing };
}

/**
 * What to assign to `document.cookie` for a decision, or the assignment that
 * removes it when there is none.
 *
 * Over TLS it is `SameSite=None; Secure; Partitioned`: the app is also shown
 * inside an embedding frame (the editor preview), where a `Lax` cookie is
 * dropped — the server then drew the consent modal on every load and the
 * buttons looked dead. Partitioned keeps it to that one embedding. Without TLS
 * (the local e2e stack) it stays `Lax`, since a `Secure` cookie is dropped there.
 */
export function consentCookieAssignment(
  value: ConsentCookie | null,
  secure: boolean,
): string {
  const attributes = secure
    ? "Path=/; SameSite=None; Secure; Partitioned"
    : "Path=/; SameSite=Lax";
  if (!value) return `${CONSENT_COOKIE}=; ${attributes}; Max-Age=0`;
  return `${CONSENT_COOKIE}=${formatConsentCookie(value)}; ${attributes}; Max-Age=${CONSENT_COOKIE_MAX_AGE}`;
}

/**
 * The button a visitor pressed on the consent surface. `reject` and `accept`
 * are whole decisions; `save` takes each category from the form.
 */
export type ConsentChoice = "reject" | "save" | "accept";

const isChoice = (value: unknown): value is ConsentChoice =>
  value === "reject" || value === "save" || value === "accept";

/**
 * The decision a submitted consent form carries, or `null` for a form that is
 * not one.
 *
 * The surface is drawn by the server and is on screen before the bundle has
 * attached to it, so its buttons submit a plain form: a press in that window is
 * a decision rather than a swallowed click. The version is the caller's because
 * the record that owns it sits a layer above this one.
 */
export function decisionFromForm(
  form: FormData,
  version: number,
): ConsentCookie | null {
  const choice = form.get("choice");
  if (!isChoice(choice)) return null;
  if (choice !== "save") {
    const decision: Decision = choice === "accept" ? "granted" : "denied";
    return { version, analytics: decision, marketing: decision };
  }
  const analytics = form.get("analytics");
  const marketing = form.get("marketing");
  if (typeof analytics !== "string" || !isDecision(analytics)) return null;
  if (typeof marketing !== "string" || !isDecision(marketing)) return null;
  return { version, analytics, marketing };
}

/**
 * Where a submitted form sends the visitor back to: the page it was submitted
 * from. Only the path is kept — behind a proxy the request's own origin is not
 * the one the visitor sees, and a redirect that carries no host cannot be
 * pointed anywhere else. Leading slashes are collapsed for the same reason:
 * `//host` is a host.
 */
export function consentReturnPath(referer: string | null): string {
  if (!referer) return "/";
  try {
    const from = new URL(referer);
    return `${from.pathname.replace(/^\/+/, "/")}${from.search}`;
  } catch {
    return "/";
  }
}
